import {
  Component, OnInit, OnDestroy, HostBinding, ChangeDetectorRef,
  ViewChildren, QueryList,
} from '@angular/core';
import { UntypedFormGroup, ValidatorFn, AbstractControl, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import * as _ from 'lodash';

import * as utils from 'utils/utils';
import * as dateUtils from 'utils/date';
import { DetailsControlType } from 'shared/details-page/details-control/details-control-type';
import { SearchModel } from 'shared/search/search.model';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { getFieldRequiredValue } from 'utils/scorecard-conditionals';

import { DetailsControlComponent } from 'shared/details-page/details-control/details-control.component';
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { BrandService } from 'shared/brand/brand.service';
import {takeWhile} from 'rxjs/operators';
import { createScoreFieldRequiredValidation } from 'shared/validation/scorecard-field-validation';


@Component({
  template: '',
})
export class BaseSegmentComponent implements OnInit, OnDestroy {
  @HostBinding('@segmentAnimation')
  segmentAnimation: any;

  @ViewChildren(DetailsControlComponent)
  detailsControls: QueryList<DetailsControlComponent>;

  // toggle controls edit/view mode
  @HostBinding('class.is-edit-mode')
  editMode: boolean = false;

  // define in the Child
  segmentForm: UntypedFormGroup;
  segmentName: string; // used in save request
  pathToScores: string;

  isActive: boolean = true;
  // score data of all segments
  scoreData: any = {};
  scorecardData: any = {};
  scorecardSegmentDataFormatted: any; // only scorecard data for segment and in formatted
  // if item_names in segment are without prefix
  // e.g. in item-details there are with prefixes - 'details.brandLabel' etc.
  // in packages there are without prefixes - 'PL.hazardous_materials.0.packing_group_code' etc.
  fieldNamesWithoutPrefix: boolean = false;

  // item data
  itemDetail: any = {};
  priceSheets: Array<any> = [];
  // used for finding controls in the row in case of table
  controlClassPrefix: string = 'control-row-';

  // bulk edit inputs
  bulkEdit: boolean = false;
  selectedIds: Array<any>;
  searchModel: SearchModel;
  listId: string;
  allSelected: boolean;

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
  ) {}

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get controlsPlaceholder(): string {
    return this.bulkEdit ? '--' : '';
  }

  get lookupPlaceholder(): string {
    return this.bulkEdit ? '--' : '-';
  }

  get optionPlaceholder(): any {
    return {option: '', title: this.lookupPlaceholder};
  }

  get brandId(): string {
    return this.brandService.getBrand();
  }

  get isDirty(): boolean {
    return _.get(this.segmentForm, 'dirty', false);
  }

  get bulkDetailsData(): any {
    return JSON.parse(localStorage.getItem('bulkDetailsData')) || {};
  }

  set bulkDetailsData(data: any) {
    localStorage.setItem('bulkDetailsData', JSON.stringify(data));
  }

  get segmentScores(): any {
    return _.get(this.scoreData, this.pathToScores);
  }

  get scorecardFields(): any {
    return _.get(this.scorecardData, `segments.${this.segmentName}.fields`);
  }

  // used to identify whether we have selected scorecard or not
  get hasScores(): boolean {
    return _.size(this.scoreData) > 0;
  }

  get itemDetailValidation(): any {
    return _.get(this.itemDetail, 'validation', {});
  }

  // used in save
  // return true, to show any warning before saving
  // showWarning method will be called
  get hasWarning(): boolean {
    return false;
  }

  // controls array in case of segment has table
  get formArray(): UntypedFormArray {
    return this.segmentForm.get(this.segmentName) as UntypedFormArray;
  }

  // segment data in the itemDetail
  get itemDetailData(): Array<any> {
    return _.get(this.itemDetail, this.segmentName);
  }

  /*
    Get all conditionals rules for segment
  */
  get scorecardConditionals(): any {
    return _.get(this.scorecardSegmentDataFormatted, 'conditionals');
  }

  // for getting conditionals
  // segmentData.segments[conditionalsSegmentName].conditionals
  // usually it's segmentName
  get conditionalsSegmentName(): string {
    return this.segmentName;
  }

  // for getting conditionals
  // segmentData.segments[conditionalsSegmentName].conditionals[i].path prefix
  // e.g. 'packages.EA.quantity'
  // usually it's segmentName
  get conditionalsFieldName(): string {
    return this.segmentName;
  }

  set segmentScores(scores: any) {
    _.set(this.scoreData, this.pathToScores, scores);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    this.scorecardConnectorService.scoreDetailsExpanded
    .pipe(takeWhile(() => this.isActive))
    .subscribe(($event: any) => {
      this.handleScoreDetailsExpand($event);
    });

    this.scorecardConnectorService.scorecardItemHovered
        .pipe(takeWhile(() => this.isActive))
    .subscribe(($event: any) => {
      this.handleScorecardItemHover($event);
    });

    this.scorecardConnectorService.scorecardItemBlured
    .pipe(takeWhile(() => this.isActive))
    .subscribe(($event: any) => {
      this.handleScorecardItemBlur($event);
    });

    // To send current statuses of conditional fields on requiring from other component
    this.scorecardConnectorService.requireConditionalStatuses
      .pipe(takeWhile(() => this.isActive))
      .subscribe(() => {
        this.handleRequireConditionalStatuses();
      });

    if (!this.scorecardSegmentDataFormatted) {
      this.updateScorecardSegmentDataFormatted(this.scorecardData);
    }
  }

  ngOnDestroy() {
    this.isActive = false;
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onControlStatusChange(status: any, item: any) {
    this.scorecardConnectorService.controlStatusChanged.emit({status, item});
  }

  onControlPopulatedChange(populated: any, item: any) {
    this.scorecardConnectorService.controlPopulatedChanged.emit({populated, item});
  }

  onControlValueChange(value: any, item: any) {
    this.updateFormValueAndValidity(this.segmentForm);
  }

  onSelectChange(value: any, item: any) {
    this.updateFormValueAndValidity(this.segmentForm);
  }

  onControlFocus(item: any, index?: number) {
    this.scorecardConnectorService.controlFocused.emit({item, index});
  }

  onCombinedControlFocus(items: Array<any>, fieldMap?: string) {
    const item = _.find(items, ['field_map', fieldMap]);
    this.scorecardConnectorService.controlFocused.emit({item});
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public toggleViewEditMode(toggle: boolean): void {
    this.editMode = toggle;
    this.ref.markForCheck();
  }

  /*
    * Called after user changes score in the dropdown
   */
  public updateScoreData(scoreData: any, scorecardData?: any): void {
    this.scoreData = scoreData;
    this.scorecardData = scorecardData;
    this.updateScorecardSegmentDataFormatted(this.scorecardData);

    _.defer(() => {
      this.updateControlsValidation();
      this.emitConditionalStatuses();
      this.ref.markForCheck();
    });
  }

  public updateItemDetail(itemDetail: any): void {
    this.itemDetail = itemDetail;
    this.handleItemDetail(this.itemDetail);
    this.markAsPristine();
    this.ref.markForCheck();
  }

  public updatePriceSheets(priceSheets: Array<any>): void {
    this.priceSheets = priceSheets;
    this.ref.markForCheck();
  }

  /*
    * Updates all segment data
   */
  public updateData(
    itemDetail?: any,
    scoreData?: any,
    scorecardData?: any,
    priceSheets?: any,
  ) {
    this.itemDetail = itemDetail;
    this.scoreData = scoreData;
    this.scorecardData = scorecardData;
    this.updateScorecardSegmentDataFormatted(this.scorecardData);
    this.priceSheets = priceSheets;
    this.handleAllDataUpdate();
    this.markAsPristine();
    this.ref.markForCheck();
  }

  public markAsDirty() {
    if (!this.segmentForm) {
      return console.warn('segmentForm is not defined!');
    }
    this.segmentForm.markAsDirty();
  }

  /*
    * Define in the child
    * Data which sends to BE on save
   */
  public getSegmentFormData(): any {
    return _.get(this, 'segmentForm.value');
  }

  /*
    * Define in the child
    * In bulk edit we save edited and saved data to the localStorage
    * To be able to see changes already made after changing segments
   */
  public getSegmentStoredBulkData(): any {
    return this.getSegmentFormData();
  }

  public markAsPristine() {
    if (!this.segmentForm) {
      return console.warn('segmentForm is not defined!');
    }
    this.segmentForm.markAsPristine();
  }

  /*
    * Stores bulk data to the lodalStorage
   */
  public storeBulkData(): void {
    const data = this.bulkDetailsData;
    _.set(data, this.segmentName, this.getSegmentStoredBulkData());
    this.bulkDetailsData = data;
  }

  /*
    * Define in the child
    * Befor saving segments-container checks hasWarning property
    * If hasWarning = true, then it calls showWarning method
   */
  public showWarning(data?: any): void {
    console.warn('showWarning not implemented');
  }

  /*
    * Used for checking combined controls group
   */
  isArray(item: any): boolean {
    return _.isArray(item);
  }

  getControlType(item: any): string {
    if (item.lookup || _.get(item, 'api_lookup.type') === 'list') {
      return DetailsControlType.Select;
    } else if (item.type && item.type === DetailsControlType.Date) {
      return DetailsControlType.Date;
    } else if (item.type === 'textarea') {
      return DetailsControlType.Textarea;
    } else {
      return DetailsControlType.Input;
    }
  }

  /*
    * Retuns validation object according on score item rule from segment scores
   */
  getItemValidation(item: SegmentFieldItem): any {
    const scoreRule = this.getItemScoreRule(item);
    return this.getItemValidationByRuleName(item, _.get(scoreRule, 'validation_rule'));
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  protected runAnimation(state: 'show'|'hide'): void {
    this.segmentAnimation = state;
    this.ref.markForCheck();
  }

  /*
    * Define in the child
    * Used to update control validations after scorecard change
  */
  protected updateControlsValidation(): void {
    console.warn('updateControlsValidation method not implemented!');
  }

  /*
    * Define in the child
    * Used to focus on control
  */
  protected handleScoreDetailsExpand($event: any): void {}

  /*
    * Used to highlight control
  */
  protected handleScorecardItemHover($event: any): void {
    const control = this.findControlViaData($event);
    if (control) { control.highlightControl(); }
  }

  /*
    * Used to unhighlight control
  */
  protected handleScorecardItemBlur($event: any): void {
    const control = this.findControlViaData($event);
    if (control) { control.unHighlightControl(); }
  }

  /*
    * Used by handleScorecardItemHover, handleScorecardItemBlur
    * Finds control using data from event
    * Define in the child
   */
  protected findControlViaData(data: any): DetailsControlComponent {
    return this.findControlByFieldMap(data.field_map);
  }

  /*
    * Init validators using item's validation object
  */
  protected initValidators(validation: any, bind?: any): any[] {
    return utils.initValidators(validation, bind);
  }

  /*
    * Define in the child
    * Handle updating item details data here
  */
  protected handleItemDetail(itemDetail: any): void {
    if (this.bulkEdit) {
      this.setBulkData();
    } else {
      this.setData(itemDetail);
    }
  }

  /*
    * Define in the child
    * Handle updating all data change
    * Called after updating all data
    * Thus itemDetail, scoreData, scorecardData, priceSheets are available here
  */
  protected handleAllDataUpdate(): void {
    if (this.bulkEdit) {
      this.setBulkData();
    } else {
      this.setData(this.itemDetail);
    }
  }

  protected findControlByFieldMap(fieldMap: string): DetailsControlComponent|undefined {
    return this.detailsControls.find((detailsControl: DetailsControlComponent) => {
      return (detailsControl.controlName === fieldMap ||
             detailsControl.fieldMap === fieldMap ||
             detailsControl.selectControlName === fieldMap);
    });
  }

  protected addLookupPlaceholder(lookup: Array<any>): any {
    if (lookup.length) {
      if (!lookup[0].option) {
        lookup[0].title = this.lookupPlaceholder;
        return lookup;
      }
    }
    lookup.unshift(this.optionPlaceholder);
    return lookup;
  }

  /*
    * Sets error when start date is ahead of end date
   */
  protected setDateAheadControlError(startDateControl: AbstractControl, endDateControl: AbstractControl): void {
    dateUtils.setDateAheadControlError(startDateControl, endDateControl);
  }

  /*
    * Sets date range to prevent select end date > start date
   */
  protected setDateDisabledRange(date: string, dateItemName: string): void {
    dateUtils.setDateDisabledRange(date, dateItemName, (<any>this).segmentData);
  }

  /*
    * Define in the Child
    * Updates segment data after itemDetail changed
   */
  protected setData(itemDetail: any): void {
    console.warn('setData method not implemented!');
  }

  /*
    * Define in the Child
    * Updates segment data in Bulk Edit (gets local storage stored data)
   */
  protected setBulkData(): void {
    this.setData(this.getBulkSegmentData(this.segmentName));
  }

  /*
    * Gets stored bulk data from local storage
   */
  protected getBulkSegmentData(segmentName: string): any {
    return _.get(this.bulkDetailsData, segmentName);
  }

  protected getItemScoreRule(item: any): any {
    return _.get(this.getItemScore(item), 'rule');
  }

  protected getItemScore(item: any): any {
    return _.get(this.segmentScores, item.item_name);
  }

  protected updateControlValidators(control: AbstractControl, validators: Array<ValidatorFn>): void {
    if (!control) { return; }
    control.clearValidators();
    control.setValidators(validators);
    control.updateValueAndValidity({emitEvent: false});
  }

  protected getFieldValue(dataItem: any, field: SegmentFieldItem): string {
    return utils.getFieldValue(dataItem, field);
  }

  protected getFieldByName(name: string, segmentData: Array<SegmentFieldItem>): any {
    return utils.getFieldByName(name, segmentData);
  }

  protected getFieldByMap(map: string, segmentData: Array<SegmentFieldItem>): any {
    return utils.getFieldByMap(map, segmentData);
  }

  protected getLookup(name: string, segmentData: Array<SegmentFieldItem>): any {
    return utils.getLookup(name, segmentData);
  }

  protected getFieldTitle(name: string, segmentData: Array<SegmentFieldItem>): string {
    const field = this.getFieldByMap(name, segmentData);
    return field && field.title ? field.title : '';
  }

  protected showSegment(): void {
    if (_.isNil(this.segmentAnimation)) {
      _.defer(() => this.runAnimation('show'));
    }
  }

  protected getScorecardField(name: string): any {
    return _.find(this.scorecardFields, (item: any) => {
      return item.path.indexOf(name) !== -1;
    });
  }

  /*
    * Returns validators according on validation rule and 'required' of the score
   */
  protected getFieldValidators(item: SegmentFieldItem): Array<ValidatorFn> {
    const score = this.getScorecardField(item.item_name);
    const validationRule = _.get(score, 'validation_rule'); // pies65, pies64, etc
    const required = _.get(score, 'required', false);
    const validation = this.getItemValidationByRuleName(item, validationRule);
    validation.conditionals = null;
    validation.required = required;

    // find all conditionals where item is in 'required' -
    // we need to find what fields it depends on
    const conditionals = this.getRequiredConditionalsForField(item.item_name); // from scorecard item, 'custom_validation_rules'

    if (conditionals) {
      validation.required = null;
      validation.conditionals = createScoreFieldRequiredValidation(conditionals);
    }

    return this.initValidators(validation, this);
  }

  /*
    * Returns validation rules by rule name (pies65, pies67, etc)
    * Or empty {} if validation is not exist
   */
  protected getItemValidationByRuleName(item: SegmentFieldItem, ruleName: string = 'pies65'): any {
    // todo check working with lists
    const validationRules = _.get(item, `validation.${ruleName}`, {});
    // pies67 === pies65
    const rules = ['pies67', 'pies70', 'pies71', 'pies72'];
    if (_.isEmpty(validationRules) && rules.includes(ruleName)) {
      return _.get(item, `validation.pies65`, {});
    }
    return validationRules;
  }

  protected getRowControls(index: number, fieldMap): Array<DetailsControlComponent> {
    return this.detailsControls.filter((detailsControl: DetailsControlComponent) => {
      return ((detailsControl.fieldMap === fieldMap || detailsControl.selectControlName === fieldMap) &&
             _.includes(detailsControl.nativeElement.classList, this.controlClassPrefix + index));
    });
  }

  protected handleRowsDrop(event: CdkDragDrop<any[]>) {
    const prev = event.previousIndex;
    const curr = event.currentIndex;
    if (prev === curr) { return; }
    const control = this.formArray.at(prev);
    this.formArray.removeAt(prev, { emitEvent: false });
    this.formArray.insert(curr, control);
    if (this.itemDetailData) {
      moveItemInArray(this.itemDetailData, prev, curr);
    }
    if (Array.isArray(this.segmentScores)) {
      moveItemInArray(this.segmentScores, prev, curr);
    }
    if (!this.isDirty) { this.markAsDirty(); }
    this.scorecardConnectorService.detectChangesInScorecard.emit();
    this.ref.markForCheck();
  }

  // replace this method in child
  /*
    This method creates form group
  */
  protected createItemFG(item: any): UntypedFormGroup {
    const itemModel = this.createItemModel(item);
    return this.fb.group(itemModel);
  }

  // replace this method in child
  /*
    This method creates model from item
  */
  protected createItemModel(item: any): any {
    return null;
  }

  /*
    Returns conditionals for fieldName
  */
  protected getRequiredConditionalsForField(fieldName: any): any {
    if (this.scorecardConditionals) {
      const pathPrefix = this.fieldNamesWithoutPrefix ? `${this.segmentName}.` : '';
      return _.find(this.scorecardConditionals, { path: `${pathPrefix}${fieldName}` });
    }
    return null;
  }

  /*
    Should perform something if other component requires for conditional statuses
  */
  protected handleRequireConditionalStatuses() {
    // if other component requires conditional statuses - emit them
    this.emitConditionalStatuses();
  }

  /*
    Emits conditional statuses of fields
    Needs to be inherited by child
  */
  protected emitConditionalStatuses() {
    // go through all conditional fields and emit their statuses
    _.forEach(this.scorecardConditionals, conditional => {
      // e.g. extendedInformation rules are in packages segment together with packages rules
      // except extra emitting of packages rules
      if (conditional.path.indexOf(`${this.segmentName}.`) !== 0) {
        return;
      }

      const fieldName = conditional.path.replace(`${this.segmentName}.`, '');

      const field = this.getFieldByName(fieldName, (<any>this).segmentData);
      if (!field) {
        return;
      }
      const formControls = _.get(this.segmentForm, 'controls');
      const formControl = _.get(formControls, `[${field.field_map}]`);

      if (!formControl) {
        return;
      }

      this.scorecardConnectorService.conditionalRequiredDataChanged.emit({
        [`${this.segmentName}.${field.item_name}`]: getFieldRequiredValue(conditional, formControls),
      });

      this.scorecardConnectorService.controlStatusChanged.emit({
        status: formControl.status,
        item: field,
      });

    });
  }

  /*
    This function converts scorecardData conditionals 'is_required_when' fields as following:

    'packages-CA-dimensions-height': null

      is converted to:

    'packages.CA.dimensions.height': { // change dashes to dots
      value: null,
      fieldMap: 'height' // from segment data - to ease using of validation function
    }
  */
  protected updateScorecardSegmentDataFormatted(scorecardData) {
    this.scorecardSegmentDataFormatted = null;
    const segmentScorecard = _.get(scorecardData, `segments[${this.conditionalsSegmentName}]`);

    if (!segmentScorecard || !segmentScorecard.conditionals) {
      return;
    }
    const scorecardSegmentDataFormatted = _.cloneDeep(segmentScorecard);

    // filter conditionals only for this segment
    // e.g. packages conditionals has conditionals for 'packages' & 'extendedInformation'
    scorecardSegmentDataFormatted.conditionals = _.filter(scorecardSegmentDataFormatted.conditionals, conditional => {
      return conditional.is_required_when && conditional.path && conditional.path.indexOf(this.conditionalsFieldName) === 0;
    });

    // go through all conditionals of segment
    _.forEach(scorecardSegmentDataFormatted.conditionals, conditional => {
      const formattedRules = {};
      // go through all `is_required_when`-rules of conditional
      _.forEach(_.keys(conditional.is_required_when), ruleKey => {

        // it is already formatted
        if (_.has(conditional.is_required_when[ruleKey], 'value')
          || _.has(conditional.is_required_when[ruleKey], 'fieldMap')) {
          formattedRules[ruleKey] = conditional.is_required_when[ruleKey];
          return;
        }

        // else - format it
        const fieldName = ruleKey.replace(/-/g, '.');
        const segmentFieldName = this.fieldNamesWithoutPrefix ?
          fieldName.replace(`${this.conditionalsFieldName}.`, '') :
          fieldName;
        const field = this.getFieldByName(segmentFieldName, (<any>this).segmentData);

        formattedRules[fieldName] = {
          value: conditional.is_required_when[ruleKey],
          fieldMap: _.get(field, 'field_map'),
        };
      });
      conditional.is_required_when = formattedRules;
    });

    this.scorecardSegmentDataFormatted = scorecardSegmentDataFormatted;
  }

  updateFormValueAndValidity(formGroup: UntypedFormGroup) {
    // If we have conditionals - we need to update formGroup validation
    // for all fields
    if (this.scorecardConditionals && formGroup) {
      _.forEach(formGroup.controls, control => {
        control.updateValueAndValidity();
      });
      this.ref.markForCheck();
    }
  }
}
